import React, { useState, useEffect, Suspense } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Layout, Menu, Button, message } from 'antd';
import { UserOutlined, LogoutOutlined, AppstoreOutlined, UnorderedListOutlined, SettingOutlined } from '@ant-design/icons';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import ProcessDetail from './pages/ProcessDetail';
import TaskDetail from './pages/TaskDetail';
import UserManagement from './pages/UserManagement';
import Settings from './pages/Settings';
import { useDispatch, useSelector } from 'react-redux';
import { setUser, clearUser } from './redux/userSlice';
import axios from 'axios';
import useIsMobile from './hooks/useIsMobile';

const LoginMobile = React.lazy(() => import('./pages/LoginMobile'));
const DashboardMobile = React.lazy(() => import('./pages/DashboardMobile'));
const ProcessStagesMobile = React.lazy(() => import('./pages/ProcessStagesMobile'));
const StageDetailMobile = React.lazy(() => import('./pages/StageDetailMobile'));

// 角色映射
const roleMap = {
  1: '测试人员',
  2: '前端开发',
  3: '后端开发',
  4: '运维人员',
  5: '产品人员',
  6: '管理员'
};

const { Header, Content, Sider } = Layout;

function App() {
  const [collapsed, setCollapsed] = useState(false);
  const dispatch = useDispatch();
  const user = useSelector(state => state.user);
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  // 检查用户登录状态
  useEffect(() => {
    const checkLoginStatus = () => {
      const token = localStorage.getItem('access_token');
      const userInfoStr = localStorage.getItem('userInfo');
      if (token) {
        // 可以在这里验证token的有效性
        // 暂时简单处理，直接设置用户信息
        const userInfo = userInfoStr ? JSON.parse(userInfoStr) : null;
        dispatch(setUser({ token, userInfo }));
      }
    };
    checkLoginStatus();
  }, [dispatch]);

  // 处理登出
  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('userInfo');
    dispatch(clearUser());
    message.success('登出成功');
  };

  // 路由配置
  const renderRoutes = () => {
    if (!user.token) {
      return (
        <Routes>
          <Route path="/login" element={isMobile ? <LoginMobile /> : <Login />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      );
    }

    return (
      <Routes>
        <Route path="/" element={isMobile ? <DashboardMobile /> : <Dashboard />} />
        <Route path="/processes/:id/stages" element={<ProcessStagesMobile />} />
        <Route path="/processes/:processId/stages/:stageId" element={<StageDetailMobile />} />
        <Route path="/process/:id" element={<ProcessDetail />} />
        <Route path="/task/:id" element={<TaskDetail />} />
        <Route path="/users" element={<UserManagement />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/login" element={<Navigate to="/" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  };

  return (
    <Layout className="glass-app">
      <Suspense fallback={<div>Loading...</div>}>
        {user.token ? (
          <>
            <Header className="glass-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div className="logo" style={{ color: '#fff', fontSize: '18px', fontWeight: 'bold' }}>
                发版系统
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <Button type="text" onClick={() => navigate('/settings')} style={{ color: 'rgba(255, 255, 255, 0.85)', padding: 0 }}>
                  {user.userInfo?.username || '用户'} ({roleMap[user.userInfo?.role_id] || '未设置'})
                </Button>
                <Button type="text" icon={<LogoutOutlined />} onClick={handleLogout} style={{ color: 'rgba(255, 255, 255, 0.85)' }}>
                  登出
                </Button>
              </div>
            </Header>
            <Layout>
              {!isMobile && (
                <Sider width={200} className="glass-sider" collapsible collapsed={collapsed} onCollapse={setCollapsed}>
                  <Menu
                    mode="inline"
                    defaultSelectedKeys={['1']}
                    style={{ height: '100%', borderRight: 0 }}
                    items={[
                      {
                        key: '1',
                        icon: <UnorderedListOutlined />,
                        label: '流程管理',
                        onClick: () => navigate('/')
                      },
                      ...(user.userInfo?.role_id === 6 ? [{
                        key: '2',
                        icon: <UserOutlined />,
                        label: '用户管理',
                        onClick: () => navigate('/users')
                      }] : [])
                    ]}
                  />
                </Sider>
              )}
              <Layout style={{ padding: isMobile ? '0 12px 12px' : '0 24px 24px' }}>
                <Content className="glass-content">
                  {renderRoutes()}
                </Content>
              </Layout>
            </Layout>
          </>
        ) : (
          <Content className="content-container">
            {renderRoutes()}
          </Content>
        )}
      </Suspense>
    </Layout>
  );
}

export default App;
